import { Trash2, Paperclip } from 'lucide-react';
import { IconButton, Card } from '@vnetwork/ui/primitives';
import type { JourneyNote } from '@/api/customer-journey';
import { formatDateTime } from '@/lib/format';

/**
 * The human notes stacked in a board column. Purely presentational — the note
 * list is derived (system + reference-linked notes filtered out) and delete is
 * delegated up so the board owns the confirm + mutation. Deletable only when a
 * note has no linked activity (reference), matching legacy.
 */
export function StageNotes({
  notes,
  canDelete,
  onDelete,
}: {
  notes: JourneyNote[];
  canDelete: boolean;
  onDelete: (note: JourneyNote) => void;
}) {
  if (!notes.length) return null;
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {notes.map((note) => (
        <Card
          key={note.customer_journey_note_id}
          flat
          style={{
            display: 'grid',
            gap: 'var(--vn-space-1)',
            padding: 'var(--vn-space-2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--vn-space-1)' }}>
            <span
              style={{
                flex: 1,
                fontSize: 'var(--vn-font-size-sm)',
                color: 'var(--vn-fg-default)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {note.customer_journey_note}
            </span>
            {canDelete ? (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Delete note"
                title="Delete note"
                onClick={() => onDelete(note)}
                style={{ flexShrink: 0, color: 'var(--vn-fg-subtle)' }}
              >
                <Trash2 size={14} />
              </IconButton>
            ) : null}
          </div>

          {note.attachment?.attachment_file_url ? (
            <a
              href={note.attachment.attachment_file_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--vn-space-1)',
                fontSize: 'var(--vn-font-size-xs)',
                color: 'var(--vn-primary-text)',
              }}
            >
              <Paperclip size={12} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {note.attachment.attachment_file_name || 'Attachment'}
              </span>
            </a>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
            {note.employee?.employee_name ? <span>{note.employee.employee_name}</span> : null}
            {note.customer_journey_notes_time ? <span>{formatDateTime(note.customer_journey_notes_time)}</span> : null}
          </div>
        </Card>
      ))}
    </div>
  );
}

export default StageNotes;
